import { useMutation, useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { ExportMenu } from '../components/room/ExportMenu';
import { HostRail } from '../components/room/HostRail';
import { PresenceBar } from '../components/room/PresenceBar';
import { RatingTable } from '../components/room/RatingTable';
import { StatusBand } from '../components/room/StatusBand';
import { StatusPill } from '../components/room/StatusPill';
import { ApiError } from '../lib/api';
import { useMe } from '../lib/auth';
import { pushToast } from '../lib/events';
import {
  clearLock,
  hideReveal,
  putField,
  reveal,
  roomQuery,
  setLock,
  type LockTargetKind,
} from '../lib/rooms';
import { useRoomRealtime } from '../lib/useRoomRealtime';
import { usePresence } from '../store/presenceStore';
import { useRoomStore } from '../store/roomStore';

// Lazy: Excalidraw is ~1.5 MB gzipped and most sessions never open the Board tab in a given visit
// (see `components/whiteboard/Whiteboard.tsx`'s own comment) — this import boundary is what keeps
// it out of the sheet-only bundle.
const Whiteboard = lazy(() => import('../components/whiteboard/Whiteboard'));

type RoomTab = 'sheet' | 'board';

/** The 404-style "off air" state — a non-member or an unknown room both answer 404, so this page
 * never says "not found" outright (that would confirm/deny existence to a non-member). */
function RoomNotFound() {
  return (
    <div className="mx-auto max-w-6xl">
      <span className="eyebrow reveal">Off air</span>
      <h1 className="display display-outline reveal mt-4 text-[clamp(4rem,14vw,9rem)]">No room</h1>
      <p className="reveal mt-4 text-text-muted">This room doesn&apos;t exist, or you&apos;re not a member of it.</p>
    </div>
  );
}

export function Room() {
  const params = useParams<{ id: string }>();
  const roomId = Number(params.id);
  const { data: me } = useMe();

  const hydrate = useRoomStore((state) => state.hydrate);
  const reset = useRoomStore((state) => state.reset);
  const rawSnapshot = useRoomStore((state) => state.snapshot);
  const cells = useRoomStore((state) => state.fields);
  const locks = useRoomStore((state) => state.locks);
  const applyFieldChange = useRoomStore((state) => state.applyFieldChange);
  const presence = usePresence(roomId);
  const [tab, setTab] = useState<RoomTab>('sheet');

  // Subscribes this room's STOMP topics for as long as the page is mounted (see
  // `lib/useRoomRealtime.ts`): buffers broadcasts until the snapshot below has hydrated the store,
  // then replays them through the store's version gates, and repeats that on every reconnect.
  const { sendFocus } = useRoomRealtime(roomId);

  // The store holds one global snapshot; navigating from room A to room B re-renders this same
  // route component (no remount) with the new `:id` before the new query's data has been hydrated
  // in, so the store can still hold room A's snapshot for a render or two. Treat that as "no
  // snapshot yet" rather than briefly rendering room A's data under room B's id (mirrors
  // `AppShell`'s `HeaderTitle` guard).
  const snapshot = rawSnapshot && String(rawSnapshot.room.id) === params.id ? rawSnapshot : null;

  const { data, isPending, error } = useQuery({ ...roomQuery(roomId), enabled: Number.isFinite(roomId) });

  useEffect(() => {
    if (data) {
      hydrate(data);
    }
  }, [data, hydrate]);

  useEffect(() => reset, [reset]);

  // No more invalidate-and-refetch after a mutation: the STOMP broadcast that follows it (see
  // `useRoomRealtime`) is what actually propagates the change, including back to the sender — the
  // only exception is the field-value cell, whose mutation response carries a `version` we can
  // write straight into the store ourselves, so the sender's own edit never waits on that
  // round-trip and never flickers.
  const putFieldMutation = useMutation({
    mutationFn: putField,
    onSuccess: (cell) => applyFieldChange(cell),
    onError: () => pushToast('Could not save that value.'),
  });
  const lockMutation = useMutation({
    mutationFn: (vars: { roomId: number; targetKind: LockTargetKind; targetId: number; locked: boolean }) =>
      vars.locked ? setLock(vars) : clearLock(vars),
    onError: () => pushToast('Could not change the lock.'),
  });
  const revealMutation = useMutation({
    mutationFn: reveal,
    onError: (mutationError: unknown) =>
      pushToast(
        mutationError instanceof ApiError && mutationError.status === 409
          ? 'That cell is empty — add a value before revealing it.'
          : 'Could not reveal that cell.',
      ),
  });
  const hideRevealMutation = useMutation({
    mutationFn: hideReveal,
    onError: () => pushToast('Could not hide the reveal.'),
  });

  if (!Number.isFinite(roomId)) {
    return <RoomNotFound />;
  }

  if (isPending) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <p className="telemetry text-text-faint">Loading room…</p>
      </div>
    );
  }

  if (error) {
    if (error instanceof ApiError && error.status === 404) {
      return <RoomNotFound />;
    }
    return (
      <p role="alert" className="telemetry text-signal">
        Could not load this room. Please retry in a moment.
      </p>
    );
  }

  if (!snapshot) {
    return null;
  }

  const { room, myRole, template } = snapshot;
  const showHostRail = myRole === 'HOST' || myRole === 'COHOST';

  return (
    <div className="mx-auto max-w-6xl">
      <div className="reveal flex items-baseline gap-4" style={{ ['--i' as string]: 0 }}>
        <span className="eyebrow">02 / Room</span>
        <span className="h-px flex-1 bg-border" aria-hidden="true" />
      </div>

      <h1 className="display reveal mt-4 text-[clamp(3.5rem,10vw,8rem)]" style={{ ['--i' as string]: 1 }}>
        {room.className}
      </h1>

      <div className="reveal mt-3 flex flex-wrap items-center gap-3" style={{ ['--i' as string]: 2 }}>
        <span className="text-text-muted">{room.guestLabel}</span>
        <span className="text-text-faint" aria-hidden="true">
          ·
        </span>
        <span className="eyebrow">{template.seriesLabel}</span>
        <StatusPill status={room.status} />
      </div>

      <div className="reveal mt-4" style={{ ['--i' as string]: 3 }}>
        <ExportMenu
          roomId={roomId}
          template={template}
          streamStartedAt={room.streamStartedAt}
          sectionTimestamps={snapshot.sectionTimestamps}
        />
      </div>

      <div className="reveal mt-4" style={{ ['--i' as string]: 4 }}>
        <PresenceBar roomId={roomId} fields={template.fields} />
      </div>

      <div className="reveal mt-10 flex flex-col gap-10 md:flex-row" style={{ ['--i' as string]: 5 }}>
        <div className="min-w-0 flex-1">
          <StatusBand
            status={room.status}
            roomId={roomId}
            template={template}
            streamStartedAt={room.streamStartedAt}
            sectionTimestamps={snapshot.sectionTimestamps}
          />
          <div role="tablist" aria-label="Room view" className="mb-6 flex gap-2">
            {(
              [
                { key: 'sheet', label: 'Sheet' },
                { key: 'board', label: 'Board' },
              ] as const
            ).map((option) => (
              <button
                key={option.key}
                type="button"
                role="tab"
                aria-selected={tab === option.key}
                onClick={() => setTab(option.key)}
                className={[
                  'min-h-tap rounded-md border px-4 text-sm font-semibold transition-colors',
                  tab === option.key
                    ? 'border-accent bg-accent-dim text-accent'
                    : 'border-border-strong text-text-muted hover:text-text',
                ].join(' ')}
              >
                {option.label}
              </button>
            ))}
          </div>

          {tab === 'sheet' ? (
            <RatingTable
              roomId={roomId}
              template={template}
              guestLabel={room.guestLabel}
              myRole={myRole}
              myUserId={me?.id ?? -1}
              status={room.status}
              cells={cells}
              locks={locks}
              activeReveal={room.activeReveal}
              presence={presence}
              onCommit={(fieldId, rowKind, value) => putFieldMutation.mutate({ roomId, fieldId, rowKind, value })}
              onToggleLock={(targetId, locked) => lockMutation.mutate({ roomId, targetKind: 'FIELD', targetId, locked })}
              onReveal={(fieldId, rowKind) => revealMutation.mutate({ roomId, fieldId, rowKind })}
              onHideReveal={() => hideRevealMutation.mutate({ roomId })}
              onFocusChange={sendFocus}
            />
          ) : (
            <Suspense
              fallback={<p className="telemetry text-text-faint">Loading board…</p>}
            >
              <Whiteboard roomId={roomId} myRole={myRole} />
            </Suspense>
          )}
        </div>
        {showHostRail && <HostRail snapshot={snapshot} />}
      </div>
    </div>
  );
}
